import * as React from 'react';
import { Icon } from '../../primitives/Icon/Icon';
import { Eye, GripVertical, MoreHorizontal, Trash2 } from '../../primitives/Icon/icons';
import { IconButton } from '../../primitives/IconButton/IconButton';
import { Menu } from '../../primitives/Menu/Menu';
import {
  root as rootCls,
  draggable as draggableCls,
  body as bodyCls,
  grip as gripCls,
  filename as filenameCls,
  meta as metaCls,
  actions as actionsCls,
} from './FileCard.css';

export interface FileCardProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children' | 'onClick'
> {
  /** The artifact filename — the card's title. */
  filename: string;
  /** The muted meta line, composed by the caller (e.g. `PDF · 40 KB · 19 Jul`). */
  meta: string;
  /** Open the file for viewing. */
  onView: () => void;
  /** Hard-delete the file. */
  onRemove: () => void;
  /** Disable Remove (e.g. while a delete is in flight). Default `false`. */
  removeDisabled?: boolean;
  /**
   * The card can be dragged somewhere that accepts it (the Composer, in the
   * app). Sets the native `draggable` attribute AND the affordance that makes
   * the gesture findable — a grip glyph and a grab cursor. An explicit prop,
   * never sniffed off `rest.draggable`: the styling and the behaviour have to
   * turn on together, and a heuristic on a spread attribute would let a caller
   * get one without the other.
   *
   * Drag is pointer-only, so it is never the ONLY route to the capability it
   * offers — the consumer owns the keyboard equivalent.
   */
  draggable?: boolean;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * FileCard — one agent-produced artifact in the Drop-6 Files rail: a bold
 * filename, a muted `type · size · date` meta line, and a "⋯" actions menu
 * (View / Remove…). Real fields only (no Replace).
 *
 * The **filename is the primary View target** — clicking a file's title is what
 * people try first. It is a real `<button>` (not a click handler on the card),
 * so the target stays keyboard-reachable and the menu trigger beside it is a
 * sibling, never a button nested in a button.
 *
 * View and Remove sit behind the menu rather than as always-visible links: a
 * destructive action one hair from the most-clicked one was too easy to hit by
 * accident (the same call the app's WorkstreamCard made). Remove is
 * danger-toned, last, and its `…` says a confirm follows — the consumer owns
 * that confirm. The trigger is named `Actions for {filename}`, so a
 * screen-reader user never hears a list of identical "More" buttons.
 */
export function FileCard({
  filename,
  meta,
  onView,
  onRemove,
  removeDisabled = false,
  draggable = false,
  className,
  ref,
  ...rest
}: FileCardProps) {
  const cls = [rootCls, draggable && draggableCls, className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} draggable={draggable} {...rest}>
      {draggable && (
        // Decoration: the drag gesture it advertises has no keyboard
        // equivalent here, so announcing it would promise a screen-reader
        // user something this card cannot give them. The consumer's keyboard
        // route (the composer's `/` typeahead) is where that capability
        // actually lives.
        <span className={gripCls} aria-hidden="true">
          <Icon as={GripVertical} size={14} />
        </span>
      )}
      <div className={bodyCls}>
        <button type="button" className={filenameCls} onClick={onView}>
          {filename}
        </button>
        <div className={metaCls}>{meta}</div>
      </div>
      <span className={actionsCls}>
        <Menu
          aria-label={`Actions for ${filename}`}
          placement="bottom-end"
          minWidth={160}
          trigger={
            <IconButton
              aria-label={`Actions for ${filename}`}
              tooltip="More actions"
              icon={<Icon as={MoreHorizontal} size={14} />}
              size="sm"
            />
          }
          items={[
            { id: 'view', label: 'View', icon: <Icon as={Eye} size={16} />, onSelect: onView },
            { separator: true },
            {
              id: 'remove',
              label: 'Remove…',
              icon: <Icon as={Trash2} size={16} />,
              tone: 'danger',
              disabled: removeDisabled,
              onSelect: onRemove,
            },
          ]}
        />
      </span>
    </div>
  );
}
FileCard.displayName = 'FileCard';
